import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';

const STATUS_OPTIONS = [
  { value: 'new',       label: 'Neu',         bg: '#dbeafe', text: '#1e40af' },
  { value: 'reviewing', label: 'In Prüfung',  bg: '#fef9c3', text: '#854d0e' },
  { value: 'invited',   label: 'Eingeladen',  bg: '#dcfce7', text: '#166534' },
  { value: 'rejected',  label: 'Abgelehnt',   bg: '#fee2e2', text: '#991b1b' },
];

function statusStyle(value) {
  return STATUS_OPTIONS.find(s => s.value === value) || STATUS_OPTIONS[0];
}

export default function JobDetail({ job, onBack }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchApplications();
  }, [job.id]);

  const fetchApplications = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('applications')
        .select('*')
        .eq('job_id', job.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setApplications(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (appId, newStatus) => {
    setUpdatingId(appId);
    try {
      const { error } = await supabase
        .from('applications')
        .update({ status: newStatus })
        .eq('id', appId);
      if (error) throw error;
      setApplications(prev =>
        prev.map(a => a.id === appId ? { ...a, status: newStatus } : a)
      );
      if (selected?.id === appId) setSelected(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      alert('Fehler beim Aktualisieren: ' + err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  const renderAnswers = (answers) => {
    if (!answers) return <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>Keine Antworten vorhanden.</p>;
    if (typeof answers === 'string') {
      try { answers = JSON.parse(answers); } catch { return <p>{answers}</p>; }
    }
    if (Array.isArray(answers)) {
      return answers.map((item, i) => (
        <div key={i} style={{ marginBottom: '16px' }}>
          <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#374151', marginBottom: '4px' }}>
            {item.question || `Frage ${i + 1}`}
          </div>
          <div style={{
            background: '#f8f9fc',
            borderRadius: '8px',
            padding: '10px 14px',
            fontSize: '0.9rem',
            color: '#111827',
            border: '1px solid #e5e7eb'
          }}>
            {item.answer ?? '–'}
          </div>
        </div>
      ));
    }
    if (typeof answers === 'object') {
      return Object.entries(answers).map(([key, val], i) => (
        <div key={i} style={{ marginBottom: '16px' }}>
          <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#374151', marginBottom: '4px' }}>
            {key}
          </div>
          <div style={{
            background: '#f8f9fc',
            borderRadius: '8px',
            padding: '10px 14px',
            fontSize: '0.9rem',
            color: '#111827',
            border: '1px solid #e5e7eb'
          }}>
            {typeof val === 'object' ? JSON.stringify(val) : String(val ?? '–')}
          </div>
        </div>
      ));
    }
    return <p>{String(answers)}</p>;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fc' }}>
      {/* Topbar */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '0 32px',
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
      }}>
        <button
          onClick={onBack}
          style={{
            padding: '7px 14px',
            background: 'transparent',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.875rem',
            color: '#374151',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          ← Dashboard
        </button>
        <div style={{ fontSize: '1.4rem', fontWeight: '800' }}>
          recrui<span style={{ color: '#667eea' }}>fly</span>
        </div>
      </div>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '40px 24px' }}>
        {/* Job Header */}
        <div style={{
          background: 'white',
          borderRadius: '14px',
          padding: '28px 32px',
          marginBottom: '28px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
        }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '800', margin: '0 0 8px', color: '#111827' }}>
            {job.position || job.title || 'Unbenannte Stelle'}
          </h1>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', color: '#6b7280', fontSize: '0.9rem' }}>
            {job.company  && <span>🏢 {job.company}</span>}
            {job.location && <span>📍 {job.location}</span>}
            {(job.contract_type || job.contractType) && <span>📄 {job.contract_type || job.contractType}</span>}
          </div>
        </div>

        {/* Main layout */}
        <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>

          {/* Applicant list */}
          <div style={{ flex: '1 1 380px', minWidth: 0 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0, color: '#111827' }}>
                Bewerbungen
                {!loading && (
                  <span style={{
                    marginLeft: '10px',
                    background: '#667eea',
                    color: 'white',
                    borderRadius: '20px',
                    padding: '2px 10px',
                    fontSize: '0.8rem',
                    fontWeight: '600'
                  }}>
                    {applications.length}
                  </span>
                )}
              </h2>
            </div>

            {loading && (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af' }}>
                ⏳ Bewerbungen laden...
              </div>
            )}

            {error && (
              <div style={{ padding: '14px', background: '#fee2e2', borderRadius: '10px', color: '#dc2626' }}>
                Fehler: {error}
              </div>
            )}

            {!loading && !error && applications.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '60px 24px',
                background: 'white',
                borderRadius: '14px',
                border: '2px dashed #e5e7eb',
                color: '#9ca3af'
              }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>📭</div>
                <p style={{ margin: 0 }}>Noch keine Bewerbungen eingegangen.</p>
              </div>
            )}

            {!loading && !error && applications.map(app => {
              const st = statusStyle(app.status);
              const isActive = selected?.id === app.id;
              return (
                <div
                  key={app.id}
                  onClick={() => setSelected(isActive ? null : app)}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '18px 20px',
                    marginBottom: '12px',
                    border: isActive ? '2px solid #667eea' : '1px solid #e5e7eb',
                    boxShadow: isActive ? '0 0 0 3px rgba(102,126,234,0.15)' : '0 1px 3px rgba(0,0,0,0.06)',
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#111827', marginBottom: '4px' }}>
                        {app.name || 'Unbekannt'}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                        {app.email}
                        {app.phone && ` · ${app.phone}`}
                      </div>
                    </div>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '20px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      background: st.bg,
                      color: st.text,
                      whiteSpace: 'nowrap',
                      marginLeft: '12px'
                    }}>
                      {st.label}
                    </span>
                  </div>

                  {/* Status-Buttons */}
                  {isActive && (
                    <div style={{
                      display: 'flex',
                      gap: '6px',
                      marginTop: '14px',
                      flexWrap: 'wrap'
                    }}
                      onClick={e => e.stopPropagation()}
                    >
                      {STATUS_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          disabled={updatingId === app.id}
                          onClick={() => updateStatus(app.id, opt.value)}
                          style={{
                            padding: '5px 12px',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            border: app.status === opt.value ? `2px solid ${opt.text}` : '1px solid #e5e7eb',
                            background: app.status === opt.value ? opt.bg : 'white',
                            color: app.status === opt.value ? opt.text : '#6b7280',
                            cursor: 'pointer'
                          }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}

                  <div style={{ fontSize: '0.75rem', color: '#d1d5db', marginTop: '10px' }}>
                    {app.created_at
                      ? new Date(app.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : ''}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Answers Panel */}
          {selected && (
            <div style={{
              flex: '0 0 380px',
              background: 'white',
              borderRadius: '14px',
              padding: '28px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              position: 'sticky',
              top: '80px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#111827' }}>
                  Formular-Antworten
                </h3>
                <button
                  onClick={() => setSelected(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1.2rem',
                    color: '#9ca3af',
                    lineHeight: 1
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{
                background: '#f8f9fc',
                borderRadius: '10px',
                padding: '14px 16px',
                marginBottom: '20px',
                fontSize: '0.875rem'
              }}>
                <div style={{ fontWeight: '700', color: '#111827', marginBottom: '4px' }}>{selected.name}</div>
                <div style={{ color: '#6b7280' }}>{selected.email}</div>
                {selected.phone && <div style={{ color: '#6b7280' }}>{selected.phone}</div>}
              </div>

              <div>
                {renderAnswers(selected.answers)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
